/**
 * Regras do CRM que valem no servidor e na tela: etapas, origens,
 * normalização de textos e telefones, e comparação de nomes.
 */

/**
 * Início do CRM: 02/11/2026, 00:00 em Salvador. Atendimentos anteriores
 * (importados da Digisac) ficam fora do funil, dos relatórios e das contagens.
 */
export const CRM_INICIO = new Date('2026-11-02T03:00:00.000Z');

export const LEAD_STAGES = ['NOVO', 'COTACAO', 'NEGOCIANDO', 'FECHOU', 'PERDEU'] as const;
export type LeadStage = (typeof LEAD_STAGES)[number];

export const LEAD_STAGE_LABELS: Record<LeadStage, string> = {
  NOVO: 'Novo atendimento',
  COTACAO: 'Cotação enviada',
  NEGOCIANDO: 'Negociando',
  FECHOU: 'Fechou',
  PERDEU: 'Perdeu',
};

export const LEAD_ORIGINS = ['INSTAGRAM', 'FACEBOOK', 'GOOGLE', 'ANUNCIO', 'SITE', 'INDICACAO', 'IMPRESSO', 'WHATSAPP', 'OUTRO', 'DESCONHECIDA'] as const;
export type LeadOrigin = (typeof LEAD_ORIGINS)[number];

export const LEAD_ORIGIN_LABELS: Record<LeadOrigin, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  GOOGLE: 'Google',
  ANUNCIO: 'Anúncio pago',
  SITE: 'Site',
  INDICACAO: 'Indicação',
  IMPRESSO: 'Material impresso',
  WHATSAPP: 'WhatsApp direto',
  OUTRO: 'Outro',
  DESCONHECIDA: 'Origem desconhecida',
};

export const LEAD_FILE_KINDS = ['VOUCHER', 'BILHETE', 'SEGURO', 'DOCUMENTO', 'OUTRO'] as const;
export type LeadFileKind = (typeof LEAD_FILE_KINDS)[number];

export const LEAD_FILE_KIND_LABELS: Record<LeadFileKind, string> = {
  VOUCHER: 'Voucher',
  BILHETE: 'Bilhete',
  SEGURO: 'Seguro viagem',
  DOCUMENTO: 'Documento',
  OUTRO: 'Outro',
};

export const MOTIVOS_PERDA = ['Preço', 'Desistiu da viagem', 'Comprou em outro lugar', 'Parou de responder', 'Sem disponibilidade', 'Outro'] as const;

/**
 * Texto para comparação: minúsculas, sem acentos, sem emojis e pontuação,
 * espaços simples. "Olá, quero uma cotação! 🧳" -> "ola quero uma cotacao".
 */
export function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * A mensagem do cliente corresponde ao texto pronto de um link de origem?
 * Aceita o texto exato, com algo a mais depois ("... cotação. Somos 4") ou
 * com pequenas edições (até ~15% das palavras diferentes).
 */
export function matchesOriginMessage(message: string, key: string): boolean {
  const msg = normalizeText(message);
  if (!key || !msg) return false;
  if (msg === key || msg.startsWith(`${key} `) || msg.includes(key)) return true;
  const keyWords = key.split(' ');
  if (keyWords.length < 4) return false;
  const msgWords = new Set(msg.split(' ').slice(0, keyWords.length + 6));
  const hits = keyWords.filter((w) => msgWords.has(w)).length;
  return hits / keyWords.length >= 0.85;
}

/**
 * Telefone só com dígitos e DDI 55. Aceita "(75) 9 9812-3456", "+55 75 99812-3456",
 * "75998123456". Celulares antigos sem o 9 recebem o 9 (fixos não).
 * Retorna null se não parecer um telefone brasileiro.
 */
export function normalizePhoneBR(value: string | null | undefined): string | null {
  if (!value) return null;
  let d = value.replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  if (d.length === 10 || d.length === 11) d = `55${d}`;
  if (!d.startsWith('55') || (d.length !== 12 && d.length !== 13)) return d.length >= 8 ? d : null;
  // 55 + DDD (2) + número (8 ou 9). Celular de 8 dígitos começando com 6-9 ganha o 9.
  if (d.length === 12 && /[6-9]/.test(d[4] ?? '')) d = `${d.slice(0, 4)}9${d.slice(4)}`;
  return d;
}

/** "5575998123456" -> "(75) 9 9812-3456" */
export function formatPhoneBR(digits: string | null | undefined): string {
  if (!digits) return '';
  const m = /^55(\d{2})(9?)(\d{4})(\d{4})$/.exec(digits);
  if (!m) return digits;
  return `(${m[1]}) ${m[2] ? `${m[2]} ` : ''}${m[3]}-${m[4]}`;
}

/** Link de WhatsApp com mensagem pronta. */
export function whatsappLink(numero: string, mensagem: string): string {
  const n = normalizePhoneBR(numero) ?? numero.replace(/\D/g, '');
  return `https://wa.me/${n}?text=${encodeURIComponent(mensagem)}`;
}

const STOP_WORDS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'familia', 'sr', 'sra']);

/**
 * Semelhança entre dois nomes de 0 a 1 (palavras em comum).
 * "Ana Paula Ribeiro" x "Família Ribeiro" -> 1 (todas as palavras de um estão no outro).
 */
export function nameSimilarity(a: string, b: string): number {
  const wa = normalizeText(a).split(' ').filter((w) => w.length > 1 && !STOP_WORDS.has(w));
  const wb = normalizeText(b).split(' ').filter((w) => w.length > 1 && !STOP_WORDS.has(w));
  if (!wa.length || !wb.length) return 0;
  const sb = new Set(wb);
  const common = wa.filter((w) => sb.has(w)).length;
  return common / Math.min(wa.length, wb.length);
}

/** Tipo provável de um arquivo pelo nome. */
export function guessFileKind(fileName: string): LeadFileKind {
  const n = normalizeText(fileName);
  // Nome igual a um localizador de companhia aérea (6 letras/números maiúsculos): bilhete.
  if (/^[A-Z0-9]{6}(\b|[ _.(-])/.test(fileName.trim()) && /[A-Z]/.test(fileName.slice(0, 6))) return 'BILHETE';
  if (/\b(voucher|hotel|hospedagem|pousada|resort|reserva)\b/.test(n)) return 'VOUCHER';
  if (/\b(bilhete|eticket|e ticket|ticket|passagem|localizador|itinerario|cartao de embarque|boarding)\b/.test(n)) return 'BILHETE';
  if (/\b(seguro|assist|apolice)\b/.test(n)) return 'SEGURO';
  if (/\b(passaporte|rg|cnh|identidade|visto|documento)\b/.test(n)) return 'DOCUMENTO';
  return 'OUTRO';
}
